export type WorklogEntry<T extends { id: string }> = {
  account: T;
  originalIndex: number;
};

export type MergedAccount<T> = T & {
  resolved: boolean;
  actionable: boolean;
};

function sortByOriginalIndex<T extends { id: string }>(
  entries: WorklogEntry<T>[],
): WorklogEntry<T>[] {
  return [...entries].sort((a, b) => a.originalIndex - b.originalIndex);
}

export function mergeResolvedIntoList<T extends { id: string }>(
  freshList: T[],
  resolved: WorklogEntry<T>[],
): MergedAccount<T>[] {
  const freshIds = new Set(freshList.map(account => account.id));
  const freshEntries = freshList.map(account => ({
    ...account,
    resolved: false,
    actionable: true,
  }));
  const resolvedEntries = sortByOriginalIndex(
    resolved.filter(entry => !freshIds.has(entry.account.id)),
  );

  const merged: MergedAccount<T>[] = [];
  let freshIndex = 0;
  let resolvedIndex = 0;

  while (freshIndex < freshEntries.length || resolvedIndex < resolvedEntries.length) {
    const nextResolved = resolvedEntries[resolvedIndex];
    if (nextResolved && nextResolved.originalIndex <= merged.length) {
      merged.push({ ...nextResolved.account, resolved: true, actionable: false });
      resolvedIndex += 1;
      continue;
    }

    if (freshIndex < freshEntries.length) {
      merged.push(freshEntries[freshIndex]);
      freshIndex += 1;
      continue;
    }

    merged.push({ ...nextResolved.account, resolved: true, actionable: false });
    resolvedIndex += 1;
  }

  return merged;
}
